import React, { useState, useEffect, useCallback, useReducer } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Box, Typography, TextField, IconButton, InputAdornment, Grid, TableSortLabel, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { medicsService } from '../../services/medicsService';
import { SortingOrder } from '../../models/enums/sortingOrder';
import { MedicTableViewDto } from '../../models/medics/medicTableViewDto';
import { ROUTES } from '../../constants/routes';
import AcceptLabel from '../common/labels/AcceptLabel';
import DenyLabel from '../common/labels/DenyLabel';
import TmPlus from '../../icons/TmPlus';
import { CategoryType } from '../../models/enums/categoryType';
import { PermissionType } from '../../models/enums/permissionType';
import { ShowForPermission } from '../common/permissions/ShowForPermission';
import { EditMedicModal } from './edit/EditMedicModal';
import { filterStorageService } from '../../services/filterStorageService';
import { FilterSection } from '../../models/enums/filterSection';

const PAGE_REFRESH_INTERVAL = 20000;

const initialState = {
    medics: [] as MedicTableViewDto[],
    page: 0,
    rowsPerPage: 15,
    dataCount: 0,
    loading: true,
    sortColumn: 'id',
    sortOrder: SortingOrder.Asc,
    loadingColumn: null as string | null,
    filter: {
        fio: '',
        hideDismissed: null as boolean | null,
    },
    shouldFetch: true,
};

type State = typeof initialState;

type Action =
    | { type: 'SET_MEDICS'; payload: { medics: MedicTableViewDto[]; dataCount: number } }
    | { type: 'SET_PAGE'; payload: number }
    | { type: 'SET_ROWS_PER_PAGE'; payload: number }
    | { type: 'SET_FILTER'; payload: Partial<State['filter']> }
    | { type: 'SET_SORT'; payload: { column: string; order: SortingOrder } }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_SHOULD_FETCH'; payload: boolean };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'SET_MEDICS':
            return {
                ...state,
                medics: action.payload.medics,
                dataCount: action.payload.dataCount,
                loading: false,
                loadingColumn: null,
                shouldFetch: false,
            };
        case 'SET_PAGE':
            return { ...state, page: action.payload, loading: true, shouldFetch: true };
        case 'SET_ROWS_PER_PAGE':
            return { ...state, rowsPerPage: action.payload, page: 0, loading: true, shouldFetch: true };
        case 'SET_FILTER':
            return {
                ...state,
                filter: { ...state.filter, ...action.payload },
                page: 0,
                loading: true,
                shouldFetch: true,
            };
        case 'SET_SORT':
            return {
                ...state,
                sortColumn: action.payload.column,
                sortOrder: action.payload.order,
                loading: true,
                loadingColumn: action.payload.column,
                shouldFetch: true,
            };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_SHOULD_FETCH':
            return { ...state, shouldFetch: action.payload };
        default:
            return state;
    }
};

export const MedicsTable = () => {
    const [isAddPopupOpen, setAddPopupOpen] = useState(false);
    const [state, dispatch] = useReducer(reducer, initialState, (baseState) => ({
        ...baseState,
        filter: filterStorageService.loadFilter(FilterSection.Medics, baseState.filter),
    }));

    const navigate = useNavigate();

    const fetchMedics = useCallback(() => {
        if (!state.shouldFetch) {
            return;
        }

        dispatch({ type: 'SET_LOADING', payload: true });

        medicsService.getByFilter({
            page: state.page,
            pageSize: state.rowsPerPage,
            fio: state.filter.fio,
            hideDismissed: state.filter.hideDismissed ? state.filter.hideDismissed : undefined,
            sortColumn: state.sortColumn,
            sortOrder: state.sortOrder,
        })
            .then(r => {
                dispatch({
                    type: 'SET_MEDICS',
                    payload: {
                        medics: r.data.data.items,
                        dataCount: r.data.data.count,
                    },
                });
            })
            .catch(e => console.log(e))
            .finally(() => {
                dispatch({ type: 'SET_LOADING', payload: false });
            });
    }, [state.shouldFetch, state.page, state.rowsPerPage, state.filter, state.sortColumn, state.sortOrder]);

    useEffect(() => {
        if (state.shouldFetch) {
            fetchMedics();
        }
        const interval = setInterval(fetchMedics, PAGE_REFRESH_INTERVAL);
        return () => clearInterval(interval); // Clear interval on component unmount
    }, [state.shouldFetch, fetchMedics]);

    useEffect(() => {
        filterStorageService.saveFilter(FilterSection.Medics, state.filter);
    }, [state.filter]);

    const handleChangePage = (_event: any, newPage: number) => {
        dispatch({ type: 'SET_PAGE', payload: newPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        dispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) });
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: 'SET_FILTER', payload: { [event.target.name]: event.target.value } as Partial<State['filter']> });
    };

    const handleAddPopupToggle = () => {
        setAddPopupOpen(!isAddPopupOpen);
    };

    const handleRowClick = (_event: React.MouseEvent, medicId: number) => {
        const url = ROUTES.medics.idPath.replace(':id', String(medicId));
        navigate(url);
    };

    const handleSort = (column: string) => {
        const isAsc = state.sortColumn === column && state.sortOrder === SortingOrder.Asc;
        dispatch({
            type: 'SET_SORT',
            payload: { column, order: isAsc ? SortingOrder.Desc : SortingOrder.Asc },
        });
    };

    const renderTableSortLabel = (column: string, label: string) => (
        <TableSortLabel
            active={state.sortColumn === column}
            direction={state.sortColumn === column ? state.sortOrder : SortingOrder.Asc}
            onClick={() => handleSort(column)}
            IconComponent={state.loadingColumn === column ? () => <CircularProgress size={12} sx={{ color: '#9588e8' }} /> : undefined}
        >
            <Typography variant="body1" sx={{ color: "#7c7e92" }}>{label}</Typography>
        </TableSortLabel>
    );

    return (
        <Box>
            <Grid container pb={1} mt={2.5}>
                <Grid item xs={10} style={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                        label="ФИО"
                        name="fio"
                        value={state.filter.fio}
                        onChange={handleFilterChange}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        style={{ width: 200, marginRight: 10 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => dispatch({ type: 'SET_PAGE', payload: 0 })}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            style: {
                                borderRadius: "10px",
                            }
                        }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                id="hideDismissedCheckbox"
                                checked={state.filter.hideDismissed || false}
                                onChange={(event) => dispatch({
                                    type: 'SET_FILTER',
                                    payload: { hideDismissed: event.target.checked ? event.target.checked : null },
                                })}
                                style={{ color: '#7E5EA9' }}
                            />
                        }
                        label="Скрыть уволенных"
                    />
                </Grid>
                <Grid item xs={2} style={{ display: 'inline-block', alignItems: 'center' }}>
                    <ShowForPermission catalog={CategoryType.Medics} permission={PermissionType.Add}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton sx={{ display: "flex" }} className="list-item" onClick={handleAddPopupToggle} >
                                <TmPlus fill="none" stroke="white" />&nbsp;
                                <Typography fontWeight="bold" pr={1}>
                                    &nbsp;Добавить
                                </Typography>
                            </IconButton>
                        </Box>
                    </ShowForPermission>
                </Grid>
            </Grid>

            <TableContainer sx={{ '.MuiTableCell-root': { py: 1.5 } }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('lastName', 'Фамилия')}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('firstName', 'Имя')}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('middleName', 'Отчество')}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('phone', 'Телефон')}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('isDismissed', 'Статус')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {state.medics.map((medic) => (
                            <TableRow
                                key={medic.id}
                                onClick={(event) => handleRowClick(event, medic.id)}
                                sx={{ '&:hover': { cursor: 'pointer', backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                            >
                                <TableCell sx={{ fontWeight: "bold" }}>{medic.lastName}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{medic.firstName}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{medic.middleName}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{medic.phone}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{medic.isDismissed ? <DenyLabel>Уволен</DenyLabel> : <AcceptLabel>Работает</AcceptLabel>}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Всего: {state.dataCount}</Typography>
                <TablePagination
                    rowsPerPageOptions={[15, 30, 50]}
                    component="div"
                    count={state.dataCount}
                    rowsPerPage={state.rowsPerPage}
                    page={state.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={"Строк на странице:"}
                    labelDisplayedRows={({ from, to, count }: any) => `Показано ${from}-${to} из ${count}`}
                />
            </Box>
            <EditMedicModal onClose={handleAddPopupToggle} onRefresh={fetchMedics} isAdding={true} open={isAddPopupOpen} />
        </Box>
    );
};